import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { getComicById, getRelatedComics, formatViews } from '@/mocks/comics';
import NotFound from '@/pages/NotFound';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const [expandedDesc, setExpandedDesc] = useState(false);
  const [chapterPage, setChapterPage] = useState(1);
  const chaptersPerPage = 20;

  const comic = id ? getComicById(Number(id)) : undefined;

  if (!comic) {
    return <NotFound />;
  }

  const relatedComics = getRelatedComics(comic);
  const totalChapterPages = Math.ceil(comic.chapters.length / chaptersPerPage);
  const paginatedChapters = comic.chapters.slice(
    (chapterPage - 1) * chaptersPerPage,
    chapterPage * chaptersPerPage
  );

  return (
    <>
      <Navbar />
      <main>
        {/* Header */}
        <section className="bg-background-100/70 py-6 md:py-8 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '漫画分类', href: '/category/all' },
                { label: comic.category, href: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />
          </div>
        </section>

        {/* Comic Info */}
        <section className="bg-background-50 py-8 md:py-10 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              {/* Cover */}
              <div className="flex-shrink-0 w-full sm:w-56 mx-auto lg:mx-0">
                <div className="aspect-[2/3] rounded-xl overflow-hidden bg-background-200">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 布丁漫画韩国漫画在线阅读`}
                    title={`${comic.title} - 布丁漫画免费韩国漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Details */}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="px-2.5 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded-md">
                    {comic.category}
                  </span>
                  <span className={`px-2.5 py-0.5 text-xs font-medium rounded-md ${
                    comic.status === '已完结'
                      ? 'bg-secondary-100 text-secondary-700'
                      : 'bg-accent-100 text-accent-700'
                  }`}>
                    {comic.status}
                  </span>
                  {comic.isHot && (
                    <span className="px-2.5 py-0.5 bg-accent-100 text-accent-700 text-xs font-medium rounded-md">
                      <i className="ri-fire-line mr-0.5"></i>热门
                    </span>
                  )}
                </div>

                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 md:gap-4 mb-4 text-sm">
                  <span className="text-foreground-600">
                    <strong className="text-foreground-800">作者：</strong>{comic.author}
                  </span>
                  <span className="text-foreground-400">|</span>
                  <span className="text-foreground-600 flex items-center gap-1">
                    <strong className="text-foreground-800">评分：</strong>
                    <i className="ri-star-fill text-accent-500"></i>
                    <span className="text-accent-600 font-medium">{comic.rating}</span>
                  </span>
                  <span className="text-foreground-400">|</span>
                  <span className="text-foreground-600">
                    <i className="ri-eye-line mr-0.5"></i>
                    {formatViews(comic.views)} 次阅读
                  </span>
                  <span className="text-foreground-400">|</span>
                  <span className="text-foreground-600">
                    更新至 {comic.chapters.length} 话
                  </span>
                  <span className="text-foreground-400">|</span>
                  <span className="text-foreground-600">
                    最近更新：{comic.updateDate}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 bg-background-100 text-foreground-600 text-xs rounded-md"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100/70 rounded-xl p-4 md:p-5 mb-5">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h3>
                  <p className={`text-sm text-foreground-600 leading-relaxed ${
                    !expandedDesc && comic.description.length > 150 ? 'line-clamp-3' : ''
                  }`}>
                    {comic.description}
                  </p>
                  {comic.description.length > 150 && (
                    <button
                      onClick={() => setExpandedDesc(!expandedDesc)}
                      className="text-xs text-primary-600 hover:text-primary-700 mt-1 cursor-pointer"
                    >
                      {expandedDesc ? '收起' : '展开全部'}
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap gap-3">
                  <button className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5">
                    <i className="ri-play-circle-line"></i>
                    开始阅读
                  </button>
                  <button className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-lg font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5">
                    <i className="ri-bookmark-line"></i>
                    加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section className="bg-background-100/70 py-8 md:py-10 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              <i className="ri-list-check mr-2"></i>
              章节列表（共 {comic.chapters.length} 话）
            </h2>

            <div className="bg-background-50 rounded-xl p-4 md:p-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {paginatedChapters.map((chapter) => (
                  <Link
                    key={chapter.id}
                    to={`/comic/${comic.id}`}
                    className="flex items-center justify-between px-3 py-2 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer group"
                  >
                    <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors">
                      {chapter.title}
                    </span>
                    <span className="text-xs text-foreground-400">{chapter.updateDate}</span>
                  </Link>
                ))}
              </div>

              {totalChapterPages > 1 && (
                <div className="flex items-center justify-center gap-1 mt-6">
                  <button
                    onClick={() => setChapterPage(Math.max(1, chapterPage - 1))}
                    disabled={chapterPage === 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-sm text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalChapterPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setChapterPage(page)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        chapterPage === page
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setChapterPage(Math.min(totalChapterPages, chapterPage + 1))}
                    disabled={chapterPage === totalChapterPages}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-sm text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="bg-background-50 py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                <i className="ri-links-line mr-2"></i>
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} size="small" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Schema.org Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${comic.title} - 布丁漫画韩国漫画`,
            description: comic.description,
            author: { '@type': 'Person', name: comic.author },
            datePublished: comic.chapters[0]?.updateDate || comic.updateDate,
            dateModified: comic.updateDate,
            url: `https://budingmanhua.com/comic/${comic.id}`,
            image: comic.coverImage,
            publisher: {
              '@type': 'Organization',
              name: '布丁漫画',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://budingmanhua.com/comic/${comic.id}`,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://budingmanhua.com' },
              { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'https://budingmanhua.com/category/all' },
              { '@type': 'ListItem', position: 3, name: comic.category, item: `https://budingmanhua.com/category/${comic.category}` },
              { '@type': 'ListItem', position: 4, name: comic.title },
            ],
          }),
        }}
      />

      <Footer />
    </>
  );
}